import { Popover, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import cn from "classnames";
import Button from "@elements/button";
import Image from "next/legacy/image";
import { CartItemModel } from "@types";
import { useAppContext } from "@context/app";
import { useTranslations } from "next-intl";
import { FieldArray, Form, Formik, FormikErrors, FormikHelpers, FormikProps, FormikTouched } from "formik";
import * as Yup from "yup";
import TextIcon from "@svg/text";
import TrashIcon from "@svg/trash";
import TextInput from "@elements/fields/textInput";

interface Props {
  className?: string;
  item: CartItemModel;
}


const CustomText: React.FC<Props> = ({ className, item }) => {
  const t = useTranslations();
  // let qtyArray = new Array(item.qty).fill("");
  let qtyArray = Array.from({ length: item.qty }, (_, index) => index);

  const { setUserCart, cart } = useAppContext();
  const formRef = useRef<FormikProps<any>>(null);
  const [textList, setTextList] = useState<{
    userTexts: { text: string }[];
  }>({ userTexts: [] });



  useEffect(() => {
    setTextList({ userTexts: item.customText || [] })
  }, [item]);



  const TextSchema = Yup.object().shape({
    userTexts: Yup.array().of(
      Yup.object().shape({
        text: Yup.string()
      })
    ),
  });



  const onSubmit = (
    value: { text: string }[],
    actions: FormikHelpers<any>,
    close: () => void
  ) => {
    const _cart = [...cart];
    for (const iterator of _cart) {
      if (iterator.id === item.id) {
        const _value = [...value]
        const _texts = _value.find(v => v?.text.trim())
        iterator.customText = _texts ? value : []
      }
    }
    setUserCart([..._cart]);
    close();
  };

  const onDelete = (
    index: number
  ) => {
    const _cart = [...cart];
    for (const iterator of _cart) {
      if (iterator.id === item.id) {
        for (const [idx, x] of (iterator.customText || []).entries()) {
          if (idx === index) {
            x.text = ""
          }
        }
        const _value = [...(iterator.customText || [])]
        const _texts = _value.find(v => v?.text)
        iterator.customText = _texts ? iterator.customText : []
      }
    }
    setUserCart([..._cart]);
  };



  return (
    <Popover className={cn("relative md:max-w-[266px] max-w-full md:w-max w-full min-w-[200px]", className)} as="nav">
      {({ close }) => (
        <>
          <Transition
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Popover.Overlay className="fixed inset-0 backdrop-brightness-50 z-[60]" />
          </Transition>
          <Popover.Button className="text-gray-500 z-[20] relative focus:outline-none w-full">
            <div className={`  text-[#008AF6] w-full flex justify-center`}>
              {textList.userTexts.length ? (
                <div className=" py-2  w-full">
                  {textList.userTexts.map((t, index) => (
                    t?.text ? <div className="w-full flex items-center mb-2 flex-nowrap space-x-5" key={index}>
                      <div className=" w-[calc(100%-60px)]">
                        <p className=" text-start font-normal text-xs text-[#999] mb-2">{`Item ${index + 1} Text`}</p>
                        <p className=" text-start font-normal text-sm text-[#333] whitespace-nowrap text-ellipsis overflow-hidden">{t.text}</p>
                      </div>

                      <div className="w-10 h-10 border rounded-lg flex items-center justify-center" onClick={(e) => { e.stopPropagation(); onDelete(index) }}>
                        <TrashIcon />
                      </div>
                    </div> : null
                  ))}
                </div>
              ) : (
                <div className="flex md:border md:py-2 md:px-5 p-3 rounded-full items-center md:w-[180px] w-full justify-center">
                  <TextIcon className=" cursor-pointer mx-1" />
                  <p className=" font-normal px-1 text-sm">
                    {t("client_giftoptions_customtext")}
                  </p>
                </div>
              )}
            </div>
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 -translate-x-full"
            enterTo="opacity-100 -translate-x-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 -translate-x-0"
            leaveTo="opacity-0 -translate-x-full"
          >
            <Popover.Panel className=" flex flex-col justify-between fixed height-screen top-[0px] z-[60] left-0  w-screen overflow-hidden shadow-lg ring-1 ring-black ring-opacity-5 md:max-w-[450px] xs:max-w-xs max-w-xs bg-white transform">
              <div className="w-full pt-6 h-[calc(100%-90px)] overflow-auto relative">
                <p className=" font-bold text-xl px-3">{t("client_giftoptions_customtext")}</p>
                <div className="w-full flex flex-wrap  mt-5 px-3">
                  <div className="relative w-32 h-32 rounded-xl overflow-hidden">
                    <Image
                      src={item.image}
                      layout="fill"
                      quality={100}
                      alt=""
                    />
                  </div>
                  <div className="flex-1 px-3 py-3 flex flex-col justify-between">
                    <p className="w-full text-base font-normal text-[#333]">
                      {item.productName}
                    </p>
                    <p className="w-full text-base font-normal text-[#999] mt-4 mb-2">{`${item.qty
                      } ${item.qty > 1 ? t("client_giftoptions_items") : t("client_giftoptions_item")}`}</p>
                  </div>
                </div>

                <div className="w-full flex flex-wrap mt-10 mb-10">
                  <Formik
                    enableReinitialize
                    innerRef={formRef}
                    initialValues={textList}
                    validationSchema={() => TextSchema}
                    validateOnBlur={false}
                    onSubmit={(values, actions) => {
                      onSubmit([...values.userTexts], actions, close);
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      setFieldValue,
                      isSubmitting,
                      isValid,
                      dirty,
                    }) => (
                      <Form className="flex flex-wrap w-full m-auto form">

                        <FieldArray
                          name="userGifts"
                          render={(arrayHelpers) => {
                            return (
                              <div className="w-full">
                                {qtyArray.map((q, index) => (
                                  <div key={`${q}`}>
                                    <div className="w-full mb-5 px-2">
                                      <div className=" w-full flex flex-wrap">
                                        <div className="w-full">
                                          <TextInput
                                            name={`userTexts.${index}.text`}
                                            value={
                                              values.userTexts[index]?.text
                                            }
                                            error={
                                              (touched.userTexts &&
                                                (touched.userTexts as FormikTouched<any>[])[index]?.text &&
                                                errors.userTexts &&
                                                (errors.userTexts as FormikErrors<any>[])[index]?.text) ?
                                                (errors.userTexts as FormikErrors<any>[])[index].text : undefined
                                            }
                                            label={qtyArray.length > 1 ? (t("client_giftoptions_itemtext")).replace("@", `${index + 1}`) : (t("client_giftoptions_itemtext")).replace("@", "")}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          }}
                        />
                        <div className="w-full flex flex-wrap bg-white justify-between items-center h-20 fixed bottom-0 rounded-t-[16px] shadow-[0px_-8px_32px_rgba(0,0,0,0.12)] p-3">
                          <Button
                            type="submit"
                            isDisabled={!isValid || !dirty}
                            className="w-full h-12 font-extrabold text-base rounded-full"
                            size="slim"
                          >
                            {t("client_buttons_save")}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default CustomText;
